/**
 * `/signin` — TASK 7.4 Step 1.
 *
 * Extracted VERBATIM from the mock's `rSignin` block (L38-60): the same tree,
 * the same classes (`styles/README.md` §1/§3/§4), the same copy —
 *
 *   div[data-screen-label="Sign in"] .sm-screen-center
 *     div .sm-auth-card .sm-anim-up
 *       div  .sm-wordmark .sm-wordmark--hero      "SteamyMusic"          L41
 *       p    .sm-lede                             "Your Drive. …"        L42
 *       p    .sm-lede--dim                        "Private & invitation…" L43
 *       h1   .sm-visually-hidden [data-h1]         "Sign in"              L44
 *       ── in flight ──  div[role=status] .sm-status-card + spinner       L46-49
 *       ── idle ──       button .sm-btn--google                           L52-55
 *       p    .sm-footnote                          "Access is by invit…"  L57
 *
 * ── DELIBERATE DELETION (mock DOM delta #3) ───────────────────────────────
 * The mock's last line (L58) is
 *   `<p …>Demo: <a href="#/signin/denied">denied</a> · <a …>error</a> ·
 *    sign-in here is simulated</p>`
 * — scaffolding that (a) advertises two error screens as navigable demos and
 * (b) states the auth is fake. Shipping it would be both a UX defect and a
 * misleading security claim. It is REMOVED, along with the two testids it
 * carried (`denied-link`, `error-link`, ui-contract §3 /signin rows 2-3).
 * Recorded here so the fidelity baseline accounts for the divergence.
 *
 * ── THE AUTH IS REAL ──────────────────────────────────────────────────────
 * The mock's `_doSignin()` (L1008) faked an 1100 ms delay and flipped
 * `flags.authed`. Here the button performs the real OIDC start:
 * a full-page navigation to `GET /api/auth/google/start`, which mints
 * state + nonce + PKCE into a signed HttpOnly `oidc_tx` cookie and 302s to
 * Google (`apps/api/src/routes/auth.ts`). It MUST be a document navigation,
 * not `fetch` — the flow ends on Google's own origin.
 *
 * The in-flight card is painted BEFORE the navigation starts so the user is
 * never left with a dead button while the browser negotiates (AC 1.7).
 *
 * @module
 */

import { TESTIDS } from '@streamymusic/shared';

import { append, el } from '../../components/primitives/dom.js';
import { GoogleButton } from '../../components/primitives/Button.js';
import { Spinner } from '../../components/primitives/Skeleton.js';
import { stashReturnTo } from './guard.js';

/** The server route that begins the real Google OIDC round-trip. */
export const OIDC_START_PATH = '/api/auth/google/start';

/**
 * The mock's in-flight state (L46-49).
 *
 * @returns {HTMLElement}
 */
export function SignInPending() {
  const card = el('div', { className: 'sm-status-card', attrs: { role: 'status' } });
  return append(
    card,
    Spinner({ size: 'signin', announce: false }),
    el('span', { className: 'sm-status-card__label', text: 'Talking to Google…' }),
  );
}

/**
 * Builds the `/signin` screen.
 *
 * @param {{
 *   returnTo?: string|null,
 *   win?: Window,
 *   startSignIn?: (target: string) => void,
 * }} [options]
 *   `startSignIn` exists so tests can observe the navigation without jsdom
 *   attempting a real one; production uses `location.assign`.
 * @returns {HTMLElement}
 */
export function SignIn(options = {}) {
  const {
    returnTo = null,
    win = window,
    startSignIn = (target) => {
      win.location.assign(target);
    },
  } = options;

  const card = el('div', { className: 'sm-auth-card sm-anim-up' });
  const screen = el('div', {
    className: 'sm-screen-center',
    attrs: { 'data-screen-label': 'Sign in' },
  });

  const button = GoogleButton({
    label: 'Sign in with Google',
    testId: TESTIDS.G_SIGN_IN_WITH_GOOGLE_BUTTON,
    onClick: () => {
      // Remember where the user was headed. It cannot ride the OAuth
      // round-trip in the URL (the callback redirects to `/`), so it is
      // stashed — already allowlist-validated by `stashReturnTo`.
      if (returnTo !== null) stashReturnTo(returnTo, win);
      button.replaceWith(SignInPending());
      startSignIn(OIDC_START_PATH);
    },
  });

  append(
    card,
    el('div', { className: 'sm-wordmark sm-wordmark--hero', text: 'SteamyMusic' }), // L41
    el('p', { className: 'sm-lede', text: 'Your Drive. Your crew. Your wall of sound.' }), // L42
    el('p', {
      className: 'sm-lede--dim',
      text: 'Private & invitation-only — one shared library, streamed straight off Google Drive.',
    }), // L43
    el('h1', {
      className: 'sm-visually-hidden',
      text: 'Sign in',
      testId: TESTIDS.PAGE_HEADING,
      attrs: { tabindex: '-1', 'data-h1': '' },
    }), // L44
    button, // L52-55
    el('p', {
      className: 'sm-footnote',
      text: 'Access is by invitation only. Ask the library owner to add your email.',
    }), // L57
  );

  return append(screen, card);
}
